import { useState } from 'react';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useReduxState } from 'hooks';
import { useApiData } from 'lib/api';
import {
  EmptyIndicator,
  FlexContainer,
  MoreOptions,
  Pagination,
  ProgressBar,
  User
} from 'components/common';
import {
  Box,
  BoxOverflowWrapper,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import {
  AchievementSupervisorsAdd,
  AchievementSupervisorsConditions,
  AchievementSupervisorsRemove,
  AchievementSupervisorsSettingsButton
} from '.';

import type { GetAchievementSupervisorsRes } from 'shared-types/responses';

const AchievementSupervisors = () => {
  const {
    messages,
    pageState: { achievement },
    theme
  } = useReduxState();

  const [page, setPage] = useState(1);

  const { data, loading, updateData } =
    useApiData<GetAchievementSupervisorsRes>({
      url: `/admin/achievements/${achievement.id}/supervisors?page=${page}`,
      onlyIf: Boolean(achievement.id)
    });

  const yesProps = {
    icon: 'check' as const,
    iconStyle: { color: theme.successColor }
  };

  const noProps = {
    icon: 'ban' as const,
    iconStyle: { color: theme.dangerColor }
  };

  return (
    <>
      <Box
        headerContent={
          achievement.supervisorAccess === 'all' ? null : (
            <AchievementSupervisorsAdd
              achievementId={achievement.id}
              existingSupervisorIds={data?.existingSupervisorIds || []}
              updateData={updateData}
            />
          )
        }
        loading={loading}
        padding={0}
        title={messages.adminSupervisors}
        titleDescription={
          <FlexContainer style={{ gap: 10, flexWrap: 'wrap' }}>
            <div>
              {achievement.supervisorAccess === 'all'
                ? messages.achievementSupervisorAccessAll
                : achievement.supervisorAccess === 'added-and-conditions'
                ? messages.achievementSupervisorAccessConditions
                : messages.achievementSupervisorAccessAdded}
            </div>

            <AchievementSupervisorsSettingsButton
              updateSupervisors={updateData}
            />
          </FlexContainer>
        }
        titleTooltip={messages.achievementSupervisorInfo}
      >
        {!data?.supervisors?.length ? (
          <EmptyIndicator
            icon="sitemap"
            title={messages.adminSupervisorsEmpty}
          />
        ) : (
          <BoxOverflowWrapper>
            <BoxTable>
              <BoxTableHeader>
                <BoxTableCell>{messages.commonUser}</BoxTableCell>
                {achievement.supervisorAccess === 'added-and-conditions' ? (
                  <>
                    <BoxTableCell>{messages.adminGroupUserAdded}</BoxTableCell>
                    <BoxTableCell>
                      {messages.adminGroupUserAttribute}
                    </BoxTableCell>
                  </>
                ) : null}
                <BoxTableCell>
                  {messages.adminSupervisorsSupervisees}
                </BoxTableCell>
                <BoxTableCell
                  shrink={true}
                  sticky={true}
                  stickyAnchor="right"
                ></BoxTableCell>
              </BoxTableHeader>

              {data.supervisors.map((supervisor) => (
                <BoxTableRow key={supervisor.id}>
                  <BoxTableCell>
                    <User
                      enableProfileLink={true}
                      user={supervisor}
                      linkAs={`/admin/achievements/${achievement.id}/supervisors/${supervisor.id}`}
                      linkHref="/admin/achievements/[achievementId]/supervisors/[supervisorId]"
                    />
                  </BoxTableCell>

                  {achievement.supervisorAccess === 'added-and-conditions' ? (
                    <>
                      <BoxTableCell
                        {...(supervisor.isAddedUser ? yesProps : noProps)}
                      >
                        {supervisor.isAddedUser
                          ? messages.commonYes
                          : messages.commonNo}
                      </BoxTableCell>

                      <BoxTableCell
                        {...(supervisor.isAttributeUser ? yesProps : noProps)}
                      >
                        {supervisor.isAttributeUser
                          ? messages.commonYes
                          : messages.commonNo}
                      </BoxTableCell>
                    </>
                  ) : null}

                  <BoxTableCell>
                    {supervisor.numberOfSupervisedUsers ? (
                      <ProgressBar
                        label={getMessageWithPlaceholders({
                          message: messages.usersNumberOfUsers,
                          placeholders: {
                            numberOfUsers: `${supervisor.numberOfCompletions} / ${supervisor.numberOfSupervisedUsers}`
                          }
                        })}
                        color={theme.successColor}
                        progress={
                          supervisor.numberOfCompletions /
                          supervisor.numberOfSupervisedUsers
                        }
                        style={{ minWidth: 100, width: 'fit-content' }}
                      />
                    ) : (
                      <div
                        style={{
                          color: theme.textColorLighter,
                          fontSize: '1.3rem',
                          fontWeight: 600,
                          lineHeight: 1
                        }}
                      >
                        {messages.commonNoUsers}
                      </div>
                    )}
                  </BoxTableCell>

                  <BoxTableCell
                    shrink={true}
                    sticky={true}
                    stickyAnchor="right"
                    stickyBackgroundColor="white"
                  >
                    <MoreOptions renderInPortal={true}>
                      <AchievementSupervisorsRemove
                        achievementId={achievement.id}
                        disabled={!supervisor.isAddedUser}
                        supervisorId={supervisor.id}
                        tooltip={
                          supervisor.isAddedUser
                            ? undefined
                            : messages.achievementSupervisorAddedWithConditions
                        }
                        updateData={updateData}
                        warning={
                          supervisor.isAddedUser && supervisor.isAttributeUser
                            ? messages.achievementSupervisorAddedWithConditionsWarning
                            : undefined
                        }
                      />
                    </MoreOptions>
                  </BoxTableCell>
                </BoxTableRow>
              ))}
            </BoxTable>
          </BoxOverflowWrapper>
        )}
      </Box>

      <Pagination
        currentPage={page}
        numberOfPages={data?.pages || 1}
        onPageChange={({ page }) => setPage(page)}
      />

      {achievement.supervisorAccess === 'added-and-conditions' ? (
        <div style={{ marginTop: 50 }}>
          <AchievementSupervisorsConditions updateSupervisors={updateData} />
        </div>
      ) : null}
    </>
  );
};

export default AchievementSupervisors;
